import React, { useMemo } from "react";

import {
  FieldFilterSelect,
  TRADE_LOG_FILTER_INPUT_CLASS,
  type TradeFilterSelectOption,
} from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { SimulatorRunOriginFilter } from "./simulatorListTypes";

const ORIGIN_OPTIONS: Array<{ value: SimulatorRunOriginFilter; label: string }> = [
  { value: "all", label: "All loaded" },
  { value: "standalone", label: "From simulator" },
  { value: "report", label: "From reports" },
];

export type SimulatorListActionBarProps = {
  originFilter: SimulatorRunOriginFilter;
  onOriginFilterChange: (origin: SimulatorRunOriginFilter) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onRunNewSimulation: () => void;
  /** Shown immediately left of Run new simulation (e.g. bulk delete). */
  bulkActions?: React.ReactNode;
};

export function SimulatorListActionBar({
  originFilter,
  onOriginFilterChange,
  searchQuery,
  onSearchQueryChange,
  onRunNewSimulation,
  bulkActions,
}: SimulatorListActionBarProps): React.ReactElement {
  const originSelectOptions = useMemo<TradeFilterSelectOption[]>(
    () =>
      ORIGIN_OPTIONS.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    [],
  );

  return (
    <div data-simulator-list-action-bar="" className="w-full shrink-0">
      <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div
          data-simulator-filter-bar=""
          data-trade-log-filter-bar=""
          className="flex min-w-0 flex-1 items-center gap-3"
        >
          <div
            data-simulator-filter-bar-filters=""
            className="flex min-w-0 items-center gap-3"
          >
            <FieldFilterSelect
              filterChrome="simulator-list"
              value={originFilter}
              onValueChange={(value) =>
                onOriginFilterChange(value as SimulatorRunOriginFilter)
              }
              options={originSelectOptions}
            />
          </div>
          <Input
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder="Search simulations…"
            data-simulator-table-search=""
            className={cn(
              TRADE_LOG_FILTER_INPUT_CLASS,
              "h-8 min-w-[12rem] max-w-[18rem] shrink-0 normal-case tracking-normal",
            )}
            aria-label="Search simulations"
          />
        </div>

        <div className="flex w-full shrink-0 flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
          {bulkActions}
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="w-full shrink-0 justify-center sm:w-auto"
            onClick={onRunNewSimulation}
          >
            <span className="truncate normal-case tracking-normal">Run new simulation</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
